import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Clock, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { PriceTrackingService, PriceHistory } from '../../services/priceTrackingService';
import toast from 'react-hot-toast';

interface PriceTrackingControlsProps {
  itemId: string;
  currentPrice?: number;
  currency?: string;
  priceTrackingEnabled: boolean;
  trackingPriority: 'HIGH' | 'MEDIUM' | 'LOW';
  lastPriceCheck?: string;
  onPriceUpdate?: (newPrice: number) => void;
}

export const PriceTrackingControls: React.FC<PriceTrackingControlsProps> = ({
  itemId,
  currentPrice,
  currency = 'USD',
  priceTrackingEnabled,
  trackingPriority,
  lastPriceCheck,
  onPriceUpdate
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isEnabled, setIsEnabled] = useState(priceTrackingEnabled);
  const [priority, setPriority] = useState(trackingPriority);

  const loadPriceHistory = useCallback(async () => {
    try {
      const history = await PriceTrackingService.getPriceHistory(itemId, 10);
      setPriceHistory(history);
    } catch (error) {
      console.error('Failed to load price history:', error);
    }
  }, [itemId]);

  useEffect(() => {
    if (isEnabled) {
      loadPriceHistory();
    }
  }, [isEnabled, itemId, loadPriceHistory]);

  const handleToggleTracking = async () => {
    setIsLoading(true);
    try {
      if (isEnabled) {
        await PriceTrackingService.disablePriceTracking(itemId);
        setIsEnabled(false);
        toast.success('Price tracking disabled');
      } else {
        await PriceTrackingService.enablePriceTracking(itemId, priority);
        setIsEnabled(true);
        toast.success('Price tracking enabled');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle price tracking');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePriorityChange = async (newPriority: 'HIGH' | 'MEDIUM' | 'LOW') => {
    setIsLoading(true);
    try {
      await PriceTrackingService.updateTrackingPriority(itemId, newPriority);
      setPriority(newPriority);
      toast.success(`Priority updated to ${newPriority}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update priority');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualPriceCheck = async () => {
    setIsLoading(true);
    try {
      const priceHistory = await PriceTrackingService.manualPriceCheck(itemId);
      
      // Update the price if it changed
      if (priceHistory.price !== currentPrice) {
        onPriceUpdate?.(priceHistory.price);
        toast.success(`Price updated: ${currency} ${priceHistory.price}`);
      } else {
        toast.success('Price checked - no change');
      }
      
      // Refresh price history
      await loadPriceHistory();
    } catch (error: any) {
      toast.error(error.message || 'Failed to check price');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'text-red-600 bg-red-50 border-red-200';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatPriceChange = (change: number) => {
    if (change > 0) {
      return (
        <span className="text-red-600 flex items-center">
          <TrendingUp className="h-3 w-3 mr-1" />
          +{currency} {Math.abs(change).toFixed(2)}
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="text-green-600 flex items-center">
          <TrendingDown className="h-3 w-3 mr-1" />
          -{currency} {Math.abs(change).toFixed(2)}
        </span>
      );
    }
    return <span className="text-gray-500">No change</span>;
  };

  const formatLastCheck = (lastCheck?: string) => {
    if (!lastCheck) return 'Never checked';
    
    const date = new Date(lastCheck);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-3">
      {/* Price Tracking Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleToggleTracking}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isEnabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm font-medium text-gray-700">
            Track Price
          </span>
        </div>
        
        {isEnabled && (
          <Button
            onClick={handleManualPriceCheck}
            disabled={isLoading}
            size="sm"
            variant="outline"
            className="flex items-center space-x-1"
          >
            <Clock className="h-3 w-3" />
            <span>Check Now</span>
          </Button>
        )}
      </div>

      {/* Priority Selection */}
      {isEnabled && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600">Tracking Priority</label>
          <div className="flex space-x-2">
            {(['HIGH', 'MEDIUM', 'LOW'] as const).map((p) => (
              <button
                key={p}
                onClick={() => handlePriorityChange(p)}
                disabled={isLoading}
                className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                  priority === p
                    ? getPriorityColor(p)
                    : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status Information */}
      {isEnabled && (
        <div className="text-xs text-gray-500 space-y-1">
          <div className="flex items-center space-x-1">
            <Clock className="h-3 w-3" />
            <span>Last checked: {formatLastCheck(lastPriceCheck)}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Settings className="h-3 w-3" />
            <span>Priority: {priority} ({priority === 'HIGH' ? '3x/week' : priority === 'MEDIUM' ? '2x/week' : '1x/week'})</span>
          </div>
        </div>
      )}

      {/* Price History */}
      {isEnabled && priceHistory.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs font-medium text-gray-600 hover:text-gray-800 flex items-center space-x-1"
          >
            <span>Price History ({priceHistory.length})</span>
            <span className={`transform transition-transform ${showHistory ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          
          {showHistory && (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {priceHistory.map((entry, index) => (
                <div key={entry.id} className="flex items-center justify-between text-xs py-1 px-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-500">
                      {new Date(entry.checked_at).toLocaleDateString()}
                    </span>
                    <span className="font-medium">
                      {currency} {entry.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {entry.price_change !== undefined && entry.price_change !== 0 && (
                      formatPriceChange(entry.price_change)
                    )}
                    <span className={`px-1 py-0.5 rounded text-xs ${
                      entry.check_type === 'manual' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      {entry.check_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
        </div>
      )}
    </div>
  );
};
